import type Product from '@whitelance/shared/src/types/product/Product';

import { invokeApig } from './awsLib';
import type Sequence from '../components/sequences/types/Sequence';

type RequestFn = (props: {
  path: string;
  method?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
  headers?: { [key: string]: any };
  queryParams?: { [key: string]: any };
  body?: { [key: string]: any };
  formData?: FormData;
  publicAPI?: boolean;
  signal?: AbortSignal;
  onError?: (error: Error) => Promise<void>;
  raw?: boolean;
}) => Promise<any>;

type RequestModule = RequestFn & {
  post: RequestFn;
  get: RequestFn;
  put: RequestFn;
  delete: RequestFn;
};

export default class ApiBridge {
  private apiInstance: RequestModule;

  constructor(apiInstance = invokeApig as RequestModule) {
    this.apiInstance = apiInstance;
  }

  async generatePreSignedUploadURL({
    filesize,
    contentType,
    key,
    file,
  }: {
    filesize: number;
    contentType: string;
    key: string;
    file: any;
  }) {
    const formData = new FormData();

    formData.append('key', key);
    formData.append('contentType', contentType);
    formData.append('filesize', filesize.toString());
    formData.append('file', (await file.text()).slice(0, 2e6));

    return this.apiInstance.post({
      path: '/apis/s3',
      formData,
      publicAPI: true,
      onError: async (res: any) => {
        if (res.status === 403) {
          throw new Error('WAF');
        }

        throw await res.json();
      },
    });
  }

  composePrivateFileLink({ path }: { path: string }): Promise<string> {
    return this.apiInstance.get({
      path: '/apis/auth/s3',
      method: 'GET',
      queryParams: {
        path: btoa(path),
      },
    });
  }

  getUser(userId: string) {
    return this.apiInstance.get({ path: `/admin/users/${userId}` });
  }

  updateUser(userId: string, data: any) {
    return this.apiInstance.put({
      path: `/admin/users/${userId}`,
      body: data,
    });
  }

  createUser(data: any) {
    return this.apiInstance.post({
      path: '/admin/users',
      body: data,
    });
  }

  getCustomerOrganisations(params?: { _source: string[] }) {
    return this.apiInstance.post({
      path: '/apis/v2/auth/es',
      body: {
        query: {
          bool: {
            filter: [
              {
                term: {
                  isCustomerOrganisation: true,
                },
              },
            ],
          },
        },
        sort: [
          {
            'customerOrganisationCustomName.keyword': 'asc',
            'name.keyword': 'asc',
          },
        ],
        index: 'users',
        ...(!!params?._source?.length && { _source: params._source }),
        size: 10e3,
      },
    });
  }

  getSmsNotificationsTemplateLink() {
    return this.apiInstance.get({
      path: '/admin/misc/sms-notifications',
    });
  }

  extractAndSaveCustomData(body: {
    targetCollection: string;
    dataTypeName?: string;
  }) {
    return this.apiInstance.post({
      path: '/apis/actions/admin/extract-and-save-custom-data',
      body,
    });
  }

  exportDataTypeItem(dataTypeId: `custom_${string}`) {
    return this.apiInstance.get({
      path: `/admin/datatype/${dataTypeId}/export`,
      raw: true,
    });
  }

  triggerBullhornUsersImport() {
    return this.apiInstance.post({
      path: '/admin/users/bullhorn-import',
    });
  }

  downloadTransactionsReport(data: {
    timestampFrom: string;
    timestampTo: string;
  }) {
    return this.apiInstance.post({
      path: '/apis/actions/admin/transactions/report',
      body: data,
      raw: true,
    });
  }

  getRecruiters(params?: { _source: string[] }) {
    return this.apiInstance.post({
      path: '/apis/v2/auth/es',
      body: {
        query: {
          bool: {
            filter: [
              {
                term: {
                  subRoles: 'recruiter',
                },
              },
            ],
          },
        },
        index: 'users',
        ...(!!params?._source?.length && { _source: params._source }),
        size: 10e3,
      },
    });
  }

  triggerMassAIIntroGeneration() {
    return this.apiInstance.post({
      path: '/apis/actions/admin/freelancers/process-ai-introductions',
    });
  }

  getProducts(params: {
    search?: string;
    filters?: any;
    sort?: any;
    page: number;
    limit: number;
  }) {
    const { search, filters, sort, page, limit } = params;

    const query = {
      bool: {
        must: [
          {
            ...(search
              ? {
                  multi_match: {
                    query: search,
                    type: 'phrase',
                    fields: '*',
                  },
                }
              : {
                  match_all: {},
                }),
          },
          ...(filters
            ? Object.keys(filters).map((key) => ({
                term: {
                  [key]: filters[key],
                },
              }))
            : []),
        ],
      },
    };

    return this.apiInstance.post({
      path: '/apis/v2/auth/es',
      body: {
        query,
        sort: sort || [
          {
            createdAt: {
              order: 'desc',
            },
          },
        ],
        size: limit,
        from: (page - 1) * limit,
        index: 'products',
        ignoreStateFilter: true,
        ignoreApprovalStatus: true,
      },
    });
  }

  updateProduct(productId: Product['id'], data: Partial<Product>) {
    return this.apiInstance.put({
      path: `/apis/actions/admin/products/${productId}`,
      body: data,
    });
  }

  getViewProductLink(productId: Product['id']) {
    return this.apiInstance.post({
      path: '/admin/misc/get-product-view-link',
      body: {
        productId,
      },
    });
  }

  getSequences(): Promise<Sequence[]> {
    return this.apiInstance.get({
      path: '/admin/misc/sequence',
    });
  }

  createSequence(data: Sequence) {
    return this.apiInstance.post({
      path: '/admin/misc/sequence',
      body: data,
    });
  }

  editSequence(data: Sequence & { PK: string; SK: string }) {
    return this.apiInstance.put({
      path: '/admin/misc/sequence',
      body: data,
    });
  }
}
